import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Crown, Sparkles, Coins, Lock, Zap, Camera, Percent, Ticket,
  Loader2, Check, Copy, Store, ChevronDown, Gift,
} from 'lucide-react';
import { toast } from 'sonner';
import { usePublicAuth } from '../context/PublicAuthContext';
import api, { subscriptionApi } from '../lib/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { ExclusiveContentFeed } from '../components/fullbravx/ExclusiveContentFeed';
import { PointsMarket } from '../components/fullbravx/PointsMarket';

const FEATURE_ICONS = { sparkles: Sparkles, camera: Camera, percent: Percent, ticket: Ticket };

const SectionTitle = ({ kicker, title, right }) => (
  <div className="flex items-end justify-between gap-4 mb-8">
    <div>
      <p className="text-[10px] uppercase tracking-[0.35em] text-[#D4AF37] font-black mb-2">{kicker}</p>
      <h2 className="text-2xl sm:text-3xl font-black text-white leading-tight" style={{ fontFamily: "'Unbounded', sans-serif" }}>{title}</h2>
    </div>
    {right}
  </div>
);

export default function BenefitsPage() {
  const navigate = useNavigate();
  const { user, isAuthenticated, refreshUser } = usePublicAuth();

  const [settings, setSettings] = useState(null);
  const [trialStatus, setTrialStatus] = useState(null);
  const [content, setContent] = useState({ posts: [], my_points: 0, is_subscriber: false });
  const [market, setMarket] = useState({ benefits: [], my_points: 0 });
  const [redemptions, setRedemptions] = useState([]);
  const [partnerBenefits, setPartnerBenefits] = useState([]);
  const [revealedCode, setRevealedCode] = useState(null);
  const [subscribing, setSubscribing] = useState(false);
  const [loading, setLoading] = useState(true);

  const isSubscriber = user?.subscription_active || content.is_subscriber;
  const usedTrial = trialStatus?.used_free_trial;
  const canStartTrial = trialStatus?.can_start_trial && settings?.trial_days > 0 && !usedTrial;
  const myPoints = market.my_points || content.my_points || 0;
  const price = settings?.price_ars || 1500;

  const loadData = useCallback(async () => {
    try {
      const [settingsRes, contentRes, marketRes] = await Promise.all([
        subscriptionApi.getSettings(),
        api.get('/fullbravx/content').catch(() => ({ data: { posts: [] } })),
        api.get('/fullbravx/benefits').catch(() => ({ data: { benefits: [] } })),
      ]);
      setSettings(settingsRes.data);
      setContent(contentRes.data);
      setMarket(marketRes.data);
      if (isAuthenticated) {
        subscriptionApi.getTrialStatus().then(r => setTrialStatus(r.data)).catch(() => {});
        api.get('/fullbravx/my-redemptions').then(r => setRedemptions(r.data.redemptions || [])).catch(() => {});
        (user?.subscription_active ? subscriptionApi.getMyPartnerBenefits() : subscriptionApi.getPartnerBenefits())
          .then(r => Array.isArray(r.data) && setPartnerBenefits(r.data)).catch(() => {});
      } else {
        subscriptionApi.getPartnerBenefits().then(r => Array.isArray(r.data) && setPartnerBenefits(r.data)).catch(() => {});
      }
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadData(); }, [loadData]);

  const handleStartFreeTrial = async () => {
    setSubscribing(true);
    try {
      const res = await subscriptionApi.startFreeTrial();
      if (res.data.success) {
        toast.success(res.data.message || '¡Tu prueba gratuita ha comenzado!');
        await refreshUser();
        loadData();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al iniciar la prueba');
      if (e?.response?.status === 400) setTrialStatus(p => ({ ...p, can_start_trial: false, used_free_trial: true }));
    } finally { setSubscribing(false); }
  };

  const handleSubscribe = async () => {
    setSubscribing(true);
    try {
      const res = await subscriptionApi.subscribeWithRebill({
        success_url: `${window.location.origin}/mi-cuenta?payment=success`,
        failure_url: `${window.location.origin}/fullbravx?payment=failed`,
      });
      if (res.data?.checkout_url) window.location.href = res.data.checkout_url;
      else toast.error('No se pudo iniciar el pago');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al procesar la suscripción');
    } finally { setSubscribing(false); }
  };

  const handleMainCta = () => {
    if (!isAuthenticated) { toast.info('Iniciá sesión para continuar'); navigate(`/iniciar-sesion?redirect=${encodeURIComponent('/fullbravx')}`); return; }
    if (isSubscriber) { toast.info('Ya sos miembro FULL BRAVX'); return; }
    if (canStartTrial) handleStartFreeTrial(); else handleSubscribe();
  };

  const revealPartnerCode = async (benefit) => {
    if (!isSubscriber) return toast.info('Los códigos de partners son para miembros FULL BRAVX');
    try {
      const res = await subscriptionApi.getBenefitCode(benefit.id);
      setRevealedCode({ ...benefit, ...res.data });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo obtener el código');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505]"><Navbar />
        <div className="min-h-[70vh] flex items-center justify-center"><Loader2 size={36} className="animate-spin text-[#D4AF37]" /></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="fullbravx-page">
      <SEO title="FULL BRAVX — Membresía, contenido exclusivo y beneficios" description="Sumate a FULL BRAVX: contenido exclusivo, canje de puntos, descuentos y acceso prioritario a los mejores eventos." />
      <Navbar />

      {/* ============ HERO ============ */}
      <style>{`
        @keyframes fbxShine { 0% { background-position: -200% center; } 100% { background-position: 200% center; } }
        @keyframes fbxMarquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        @keyframes fbxSpin { to { transform: rotate(360deg); } }
        @keyframes fbxFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
        .fbx-shine { background: linear-gradient(110deg, #D4AF37 20%, #FFF6D8 40%, #D4AF37 60%); background-size: 200% auto; -webkit-background-clip: text; background-clip: text; color: transparent; animation: fbxShine 4s linear infinite; }
        .fbx-ring { background: conic-gradient(from 0deg, transparent 0%, rgba(212,175,55,0.55) 12%, transparent 30%, transparent 55%, rgba(212,175,55,0.3) 70%, transparent 85%); animation: fbxSpin 9s linear infinite; }
      `}</style>
      <section className="relative overflow-hidden">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[700px] h-[700px] rounded-full bg-[#D4AF37]/10 blur-[140px] pointer-events-none" />
        <div className="absolute top-24 -left-32 w-[380px] h-[380px] rounded-full bg-[#8B5CF6]/[0.07] blur-[120px] pointer-events-none" />
        <div className="absolute inset-0 opacity-[0.05] pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '26px 26px' }} />

        <div className="relative max-w-6xl mx-auto px-5 pt-16 pb-10 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            {/* Crown in spinning ring */}
            <div className="relative w-24 h-24 mx-auto mb-6" style={{ animation: 'fbxFloat 5s ease-in-out infinite' }}>
              <div className="absolute inset-0 rounded-full fbx-ring" />
              <div className="absolute inset-[3px] rounded-full bg-[#0A0A0A] border border-[#D4AF37]/30 flex items-center justify-center shadow-[0_0_45px_rgba(212,175,55,0.3)]">
                <Crown size={34} className="text-[#D4AF37]" />
              </div>
            </div>

            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#D4AF37]/40 bg-[#D4AF37]/10 text-[#D4AF37] text-[10px] font-black uppercase tracking-[0.3em] mb-6">
              <Sparkles size={12} /> Membresía oficial de la escena
            </span>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black leading-[0.92] mb-5" style={{ fontFamily: "'Unbounded', sans-serif" }}>
              FULL <span className="fbx-shine">BRAVX</span>
            </h1>
            <p className="text-white/50 text-base md:text-lg max-w-xl mx-auto mb-8">
              Contenido que nadie más ve, beneficios que se canjean con puntos y acceso prioritario a los mejores eventos.
            </p>

            {isSubscriber ? (
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-emerald-500/10 border border-emerald-500/40 text-emerald-400 font-bold text-sm" data-testid="member-badge">
                <Check size={16} /> Sos miembro FULL BRAVX
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={handleMainCta}
                  disabled={subscribing}
                  className="relative px-10 py-4 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#F2D272] text-black font-black uppercase tracking-widest text-sm shadow-[0_0_50px_rgba(212,175,55,0.45)]"
                  data-testid="fullbravx-main-cta"
                >
                  {subscribing ? 'Procesando…' : canStartTrial ? `Probar ${settings.trial_days} días gratis` : `Suscribirme · $${price.toLocaleString('es-AR')}/mes`}
                </motion.button>
                {canStartTrial && <p className="text-white/35 text-xs">Sin tarjeta. Después ${price.toLocaleString('es-AR')}/mes, cancelás cuando quieras.</p>}
              </div>
            )}

            {/* Points chip */}
            {isAuthenticated && (
              <div className="mt-8 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm" data-testid="my-points-chip">
                <Coins size={15} className="text-[#D4AF37]" />
                <span className="text-white font-black">{myPoints.toLocaleString()}</span>
                <span className="text-white/40 text-xs uppercase tracking-wider ml-1">puntos BRAVX para canjear</span>
              </div>
            )}
          </motion.div>
        </div>

        {/* Marquee strip */}
        <div className="relative border-y border-[#D4AF37]/20 bg-[#D4AF37]/[0.04] py-3 overflow-hidden mb-6" data-testid="fbx-marquee">
          <div className="flex whitespace-nowrap w-max" style={{ animation: 'fbxMarquee 22s linear infinite' }}>
            {[0, 1].map(dup => (
              <div key={dup} className="flex items-center">
                {['Contenido exclusivo', 'Canje de puntos', 'Acceso prioritario', 'Descuentos en partners', 'Fotos HD sin marca', 'Auras premium', 'Aftermovies backstage', 'Beneficios en eventos'].map((t, i) => (
                  <span key={i} className="flex items-center gap-3 px-5 text-[11px] font-black uppercase tracking-[0.3em] text-[#D4AF37]/80">
                    {t} <Sparkles size={10} className="text-[#D4AF37]/50" />
                  </span>
                ))}
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-5 pb-24 space-y-20">
        {/* ============ EXCLUSIVE CONTENT ============ */}
        {content.posts.length > 0 && (
          <section id="contenido">
            <SectionTitle kicker="Solo para miembros" title="Contenido exclusivo" right={!isSubscriber && <span className="hidden sm:inline-flex items-center gap-1.5 text-white/35 text-xs"><Lock size={12} /> Suscribite para ver todo</span>} />
            <ExclusiveContentFeed
              posts={content.posts}
              myPoints={myPoints}
              isSubscriber={isSubscriber}
              isAuthenticated={isAuthenticated}
              onRefresh={loadData}
              onSubscribeClick={handleMainCta}
            />
          </section>
        )}

        {/* ============ POINTS MARKET ============ */}
        <section id="canje">
          <SectionTitle
            kicker="Canjeá tus puntos"
            title="Marketplace de beneficios"
            right={isAuthenticated && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/40 text-[#D4AF37] text-xs font-black"><Coins size={12} /> {myPoints.toLocaleString()} pts</span>}
          />
          <p className="text-white/40 text-sm -mt-4 mb-8 max-w-2xl">Ganás puntos comprando entradas y cuando alguien usa tu @ en el checkout. Acá los convertís en premios reales.</p>
          <PointsMarket
            benefits={market.benefits || []}
            myPoints={myPoints}
            isSubscriber={isSubscriber}
            isAuthenticated={isAuthenticated}
            redemptions={redemptions}
            onRefresh={loadData}
          />
        </section>

        {/* ============ MEMBERSHIP FEATURES ============ */}
        <section id="incluye">
          <SectionTitle kicker="La membresía" title="Todo lo que incluye" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {(settings?.benefits || []).map((f, i) => {
              const Icon = FEATURE_ICONS[f.icon] || Zap;
              return (
                <motion.div key={f.id || i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                  className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 hover:border-[#D4AF37]/40 transition-colors">
                  <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center mb-4">
                    <Icon size={18} className="text-[#D4AF37]" />
                  </div>
                  <h3 className="text-white font-bold text-sm mb-1">{f.title}</h3>
                  <p className="text-white/40 text-xs leading-relaxed">{f.description}</p>
                </motion.div>
              );
            })}
            <motion.div initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
              className="rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#D4AF37]/10 to-transparent p-6">
              <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center mb-4">
                <Lock size={18} className="text-[#D4AF37]" />
              </div>
              <h3 className="text-white font-bold text-sm mb-1">Contenido exclusivo</h3>
              <p className="text-white/40 text-xs leading-relaxed">Aftermovies, anuncios y material que solo ven los miembros.</p>
            </motion.div>
          </div>
        </section>

        {/* ============ PARTNER BENEFITS ============ */}
        {partnerBenefits.length > 0 && (
          <section id="partners">
            <SectionTitle kicker="Partners" title="Descuentos con nuestra red" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {partnerBenefits.map((b, i) => (
                <motion.div key={b.id} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }}
                  className="rounded-2xl border border-white/10 bg-white/[0.03] overflow-hidden hover:border-[#D4AF37]/40 transition-colors" data-testid={`partner-benefit-${b.id}`}>
                  {b.image_url && <img src={b.image_url} alt="" className="w-full h-28 object-cover" />}
                  <div className="p-5">
                    <p className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold mb-1 flex items-center gap-1"><Store size={10} /> {b.partner_name}</p>
                    <h3 className="text-white font-bold text-sm mb-1">{b.title}</h3>
                    {b.description && <p className="text-white/40 text-xs mb-4 line-clamp-2">{b.description}</p>}
                    <button
                      onClick={() => revealPartnerCode(b)}
                      className={`w-full px-4 py-2.5 rounded-full text-xs font-black uppercase tracking-widest transition-colors ${isSubscriber ? 'bg-[#D4AF37] text-black hover:bg-[#F2D272]' : 'bg-white/5 text-white/30 border border-white/10'}`}
                      data-testid={`partner-code-btn-${b.id}`}
                    >
                      {isSubscriber ? (b.code_redeemed ? 'Ver mi código' : 'Obtener código') : 'Solo miembros'}
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* ============ FINAL CTA ============ */}
        {!isSubscriber && (
          <section className="text-center rounded-3xl border border-[#D4AF37]/30 bg-gradient-to-b from-[#D4AF37]/10 to-transparent px-6 py-14">
            <Gift size={36} className="mx-auto text-[#D4AF37] mb-4" />
            <h2 className="text-2xl sm:text-3xl font-black mb-3" style={{ fontFamily: "'Unbounded', sans-serif" }}>La escena completa te espera</h2>
            <p className="text-white/45 text-sm max-w-md mx-auto mb-7">
              {canStartTrial ? `Probá ${settings.trial_days} días gratis, sin tarjeta.` : `Por $${price.toLocaleString('es-AR')}/mes accedés a todo.`} Cancelás cuando quieras.
            </p>
            <button onClick={handleMainCta} disabled={subscribing}
              className="px-10 py-4 rounded-full bg-gradient-to-r from-[#D4AF37] to-[#F2D272] text-black font-black uppercase tracking-widest text-sm hover:scale-[1.03] transition-transform"
              data-testid="fullbravx-bottom-cta">
              {subscribing ? 'Procesando…' : canStartTrial ? 'Empezar prueba gratis' : 'Sumarme a FULL BRAVX'}
            </button>
          </section>
        )}
      </div>

      {/* Partner code modal */}
      {revealedCode && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setRevealedCode(null)}>
          <div className="bg-[#0E0E0E] border border-[#D4AF37]/50 rounded-3xl p-8 max-w-sm w-full text-center" onClick={(e) => e.stopPropagation()} data-testid="partner-code-modal">
            <p className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-bold mb-1">{revealedCode.partner_name}</p>
            <h3 className="text-white font-black text-lg mb-5">{revealedCode.title}</h3>
            <button
              onClick={() => { navigator.clipboard.writeText(revealedCode.code); toast.success('Código copiado'); }}
              className="w-full py-4 rounded-2xl bg-[#D4AF37]/10 border-2 border-dashed border-[#D4AF37]/60 font-mono text-2xl font-black tracking-[0.25em] text-[#D4AF37] flex items-center justify-center gap-3"
            >
              {revealedCode.code} <Copy size={16} />
            </button>
            <button onClick={() => setRevealedCode(null)} className="mt-5 text-white/40 text-xs hover:text-white">Cerrar</button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
